// Library Utilities
import React, { useEffect, useRef, useState } from "react";

// Constants
import warningIconSvg from "../../../assets/images/warning-icon.svg";

// Components
import { buttonStyles, StyledButton } from "../../button/button";
import ErrorMessage from "../../error-message/errorMessage";

// Styles
import Styles from "./otpVerificationPopUp.module.scss";

// Functions
import { checkIsEmpty, isValidOtp } from "../../../utils/validation";
import { testIdProps } from "../../../utils/testIdProps";
import { OTP_PURPOSE } from "../../../utils/otpApi";

function OtpVerificationPopUp({
  toggleResetOtp,
  getOtp,
  isLoading,
  onCancel,
  resendOtp,
  forZpin = false,
  error,
  setError,
}: any) {
  const registeredEmail = localStorage.getItem("email");
  const lengthOfOtp = 6;
  const [otp, setOtp] = useState("");
  const inputs: any = Array(lengthOfOtp)
    .fill(null)
    .map((_, index) => index);
  let currentActiveInput = 0;
  const inputRef = useRef<any>([]);

  const [validOtp, setValidOtp] = useState(false);
  const [isInvalid, setIsInvalid] = useState(false);
  const [resendOTPCount, setResendOTPCount] = useState(0);
  const [timer, setTimer] = useState(0);

  useEffect(() => {
    // reset the otp in case of any error occurred
    if (toggleResetOtp) {
      resetAllInputs();
    }
  }, [toggleResetOtp]);

  function validateOtp(e: any) {
    e.preventDefault();
    if (checkIsEmpty(otp)) {
      setError(error || "Otp cannot be empty");
      return setIsInvalid(true);
    }
    // setToggleResetOtp(false);
    getOtp(null, otp, setInvalidOTPError);
  }

  function setInvalidOTPError(errorMessage?: string) {
    setIsInvalid(true);
    setError(errorMessage || error || "OTP Is not valid, please re-enter");

    resetAllInputs();
    setTimeout(() => {
      setIsInvalid(false);
      setError(error || "");
    }, 2000);
  }

  function handleOtp(otpValue: string, index: number) {
    // To check whether OTP is numeric or not
    if (!isValidOtp(otpValue) && otpValue !== "") {
      inputRef.current[index].value = "";
      setError(error || "OTP should be numeric");
      setIsInvalid(true);
      return;
    }
    // To check whether the user is removing the entered OTP.
    if (otpValue === "") {
      currentActiveInput = index - 1;
      const currentInput = inputRef.current[currentActiveInput];
      // To check whether the focused input is not less than 0
      if (currentActiveInput < 0) {
        return;
      }
      // To check whether the user is clearing the last input
      // if clears the last input than we remove two values else one
      if (otp.length === lengthOfOtp) {
        // const nextInput = inputRef.current[currentActiveInput + 1];
        const str = otp.substr(0, otp.length - 2);
        setOtp(str);
      } else {
        const str = otp.substr(0, otp.length - 1);
        setOtp(str);
      }
      // Remove disable state.
      currentInput.disabled = false;
      currentInput.value = "";
    } else {
      // Only accepts 6 digits in otp
      if (otp.length > lengthOfOtp - 1) {
        return;
      }
      setOtp((otp: string) => `${otp + otpValue}`);
      const currentInput = inputRef.current[index];
      // Add disable state once input is filled.
      otp.length < lengthOfOtp - 1 && (currentInput.disabled = true);
      currentActiveInput = index + 1;
    }
    // To check the verify button enable and disable
    if (otp.length === lengthOfOtp - 1) {
      setValidOtp(true);
    } else {
      setValidOtp(false);
    }
    setError("");
    const nextInput = inputRef.current[currentActiveInput];
    // To Check whether the focus does not exceed the number of input
    // else throw error of index out of bound
    if (currentActiveInput < lengthOfOtp && currentActiveInput >= 0) {
      nextInput.focus();
    }
  }

  // reset all input values in case the user selects reset password or any error occurred
  function resetAllInputs() {
    let i = 0;
    while (i < lengthOfOtp) {
      const currentInput = inputRef.current[i];
      currentInput.value = "";
      currentInput.disabled = false;
      i++;
    }
    setOtp("");
    setValidOtp(false);
    inputRef.current[currentActiveInput].focus();
  }

  function handleResendOtp() {
    resetAllInputs();
    // Call API to handle Resend OTP;
    forZpin ? resendOtp(OTP_PURPOSE.MANAGING_ZPIN) : resendOtp();
    setResendOTPCount(resendOTPCount + 1);
    // Resend Otp to be enabled in
    let resendOTPInSec = 30;
    setTimer(resendOTPInSec);
    const otpCount = setInterval(() => {
      resendOTPInSec -= 1;
      setTimer(resendOTPInSec);
      if (resendOTPInSec === 0) {
        clearInterval(otpCount);
      }
    }, 1000);
    if (resendOTPCount >= 2) {
      setError("You have exhausted the resend OTP limit");
    }
  }

  function handlePaste(e: React.ClipboardEvent<HTMLInputElement>) {
    const pastedData = e.clipboardData.getData("Text").trim();
    const digits = pastedData
      .replace(/\D/g, "")
      .slice(0, lengthOfOtp)
      .split("");

    if (digits.length === lengthOfOtp) {
      e.preventDefault();
      setOtp(digits.join(""));
      setIsInvalid(false);
      setError("");

      digits.forEach((digit, i) => {
        const input = inputRef.current[i];
        if (input) {
          input.value = digit;
          input.disabled = i !== lengthOfOtp - 1;
        }
      });

      const lastInput = inputRef.current[lengthOfOtp - 1];
      if (lastInput) lastInput.focus();

      setValidOtp(true);
    }
  }

  return (
    <form>
      <div className="py-3 text-center">
        <img
          src={warningIconSvg}
          alt="otp-verification"
          style={{ width: "100px" }}
        />
      </div>
      <div className="pt-3 pb-1 text-center">
        <p className={`mb-0 ${Styles.headingText}`}>Enter OTP</p>
      </div>
      <div className="py-2 px-4">
        <p className={`mb-0 ${Styles.subText}`}>
          Please enter the OTP sent to <span>{registeredEmail}</span>
        </p>
      </div>
      <div style={{ width: "300px", margin: "0 auto" }}>
        <div className="py-2 d-flex align-items-center justify-content-center">
          {inputs.map((input: any, index: number) => {
            return (
              <div key={input} className="px-1">
                <input
                  ref={(el: any) => (inputRef.current[index] = el)}
                  {...testIdProps(`otp-verification-${index}`)}
                  onClick={() => {
                    setIsInvalid(false);
                    setError("");
                  }}
                  type="password"
                  className={`${Styles.commonInputClass} ${
                    Styles.formControl
                  } ${isInvalid ? Styles.wrongOtp : ""}`}
                  maxLength={1}
                  autoComplete="off"
                  autoFocus={index === 0}
                  style={{ width: "40px" }}
                  onKeyUp={(event: any) => {
                    const otpValue = event.target.value;
                    handleOtp(otpValue, index);
                  }}
                  onPaste={handlePaste}
                />
              </div>
            );
          })}
        </div>
        <div className="pb-3 px-1 d-flex align-items-center justify-content-center flex-column">
          {error && <ErrorMessage forPopup={true}>{error}</ErrorMessage>}
          {resendOTPCount <= 2 && (
            <div className={`ml-auto mr-auto ${error ? "px-2" : "p-2"}`}>
              {timer > 0 ? (
                <p className={`mb-0 ${Styles.subText}`}>
                  Resend OTP in {timer}s
                </p>
              ) : (
                <p
                  {...testIdProps("resend-otp-verify")}
                  className={`mb-0 ${Styles.resendOtpText}`}
                  onClick={handleResendOtp}
                >
                  Resend OTP
                </p>
              )}
            </div>
          )}
        </div>
      </div>
      <div className="pt-4 d-flex align-items-center justify-content-center px-4">
        <div className="px-2">
          <StyledButton
            type="submit"
            id="sumbit-otp-verify"
            hoverStyle={{
              ...(validOtp
                ? buttonStyles.primary.hover
                : buttonStyles.disabled.hover),
              width: "200px",
            }}
            style={{
              ...(validOtp
                ? buttonStyles.primary.normal
                : buttonStyles.disabled.normal),
              width: "200px",
            }}
            isLoading={isLoading}
            disabled={!validOtp}
            onClick={(e: any) => {
              setTimer(0);
              validateOtp(e);
            }}
          >
            Submit
          </StyledButton>
        </div>
      </div>
      <div className="py-3 d-flex justify-content-center">
        <div className="d-inline-flex">
          <p
            {...testIdProps("cancel-otp-verify")}
            className={`mb-0 ${Styles.cancelText}`}
            onClick={() => {
              setTimer(0);
              onCancel();
            }}
          >
            Cancel
          </p>
        </div>
      </div>
    </form>
  );
}

export default OtpVerificationPopUp;
